import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError, SESSION_EXPIRED } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  // A session that cannot be refreshed drops the user at the login screen
  // instead of leaving them on pages whose requests all quietly fail.
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener(SESSION_EXPIRED, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired);
  }, []);

  const login = async (username, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { username, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  // A Super Admin whose login is linked to an employee profile (the CEO, INT0001) can switch between
  // the admin portal and the employee view (team approvals, notifications...) without a second login.
  const [viewMode, setViewModeState] = useState(() => {
    try { return localStorage.getItem("hrm.view") || "admin"; } catch { return "admin"; }
  });
  const setViewMode = (m) => {
    setViewModeState(m);
    try { localStorage.setItem("hrm.view", m); } catch {}
  };
  const isSuperAdmin = user && user.role === "SUPER_ADMIN";
  const employeeProfile = isSuperAdmin ? user.employee || null : null;
  const hasEmployeeProfile = !!employeeProfile;
  const inEmployeeView = hasEmployeeProfile && viewMode === "employee";
  const isEmployee = user && user.role === "EMPLOYEE";
  const isProvider = user && user.role === "SERVICE_PROVIDER";

  return (
    <AuthContext.Provider value={{ user, error, login, logout, isSuperAdmin, isEmployee, isProvider, refetch: check,
      employeeProfile, hasEmployeeProfile, inEmployeeView, setViewMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
